import { BehaviorSubject, Subscription } from 'rxjs';
import { ModalService } from 'app/services/modal.service';

import { Comment } from '@kinomap-dev/ng-ui-lib/lib/models/comment.model';
import { EventEmitter } from '@angular/core';
import { WatchIntegrateModal } from 'app/modules/browse/pages/scenic/components/watchIntegrateModal/watchIntegrateModal.component';
import { WatchSharingModalComponent } from 'app/modules/browse/pages/scenic/components/watch-sharing-modal/watch-sharing-modal.component';
import { WatchLikeList } from 'app/modules/browse/pages/scenic/components/watchLikeList/watchLikeList.component';
import { WatchCommentList } from 'app/modules/browse/pages/scenic/components/watchCommentsList/watchCommentList.component';
import { WatchReportModal } from 'app/modules/browse/pages/scenic/components/watchReportModal/watchReportModal.component';
import { WatchPlaylistModal } from 'app/modules/browse/pages/scenic/components/watchPlaylistModal/watchPlaylistModal.component';
import { WatchNewPlaylistModalComponent } from 'app/modules/browse/pages/scenic/components/watchNewPlaylistModal/watchNewPlaylistModal.component';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'app/models';
import { WatchRankingModal } from 'app/modules/browse/pages/watch/components/watchRankingModal/watchRankingModal.component';
import { WatchStravaSectionsModalComponent } from 'app/modules/browse/pages/watch/components/watchStarvaSectionsModal/watchStravaSectionsModal.compoment';
import { StravaSection } from '@kinomap-dev/ng-ui-lib/lib/models/strava-section.model';
import { ScenicsModalComponent } from 'app/modules/browse/pages/watch/components/scenics-modal/scenics-modal.component';
import {
  Coaching,
  GpsOnly,
  Information,
  Top3,
  VideoSection,
  Workout,
} from '@kinomap-dev/ng-ui-lib/lib/models';
import {
  FollowType,
  ListFollowComponent,
} from 'app/modules/dashboard/components/list-follow/list-follow.component';
import { BankCardFormModalComponent } from 'app/modules/dashboard/components/bank-card-form-modal/bank-card-form-modal.component';
import { ToasterService } from 'app/services/toaster.service';
import { ConnectionModalComponent } from 'app/shared/components/modal/connection-modal/connection-modal.component';
import { SwarmLoginService } from 'app/services';
import { AllFiltersComponent } from 'app/modules/browse/components/all-filters/all-filters.component';
import { ModeNamesEnum } from 'app/modules/browse/models/browse-mode';
import { UserDescriptionComponent } from 'app/modules/dashboard/components/user-description/user-description.component';

export function configureIntegrateModal(
  modal: ModalService,
  information: any
): Subscription {
  modal.setDialogComponent(WatchIntegrateModal);
  return modal.onModalRendered.subscribe(
    (component: WatchIntegrateModal) =>
      (component.videoHashId = information.hash_id)
  );
}

export function configureSameContributorModal(
  modal: ModalService,
  items: Workout[] | VideoSection[] | Coaching[] | GpsOnly[]
): Subscription {
  modal.setDialogComponent(ScenicsModalComponent);
  return modal.onModalRendered.subscribe(
    (component: ScenicsModalComponent) => (
      (component.items = items),
      (component.title = 'watch.same_contributor_modal.title')
    )
  );
}

export function configureRecommendedModal(
  modal: ModalService,
  items: Workout[] | VideoSection[] | Coaching[] | GpsOnly[]
): Subscription {
  modal.setDialogComponent(ScenicsModalComponent);
  return modal.onModalRendered.subscribe(
    (component: ScenicsModalComponent) => (
      (component.items = items),
      (component.title = 'watch.recommended_modal.title')
    )
  );
}

export function configureShareModal(modal: ModalService): Subscription {
  modal.setDialogComponent(WatchSharingModalComponent);
  return modal.onModalRendered.subscribe(
    (component: WatchSharingModalComponent) => (component.copied = false)
  );
}

export function configureFollowsModal(
  modal: ModalService,
  followType: FollowType,
  user: User,
  modalClassName: string,
  isExternal?: boolean
): Subscription {
  modal.setDialogComponent(ListFollowComponent);
  return modal.onModalRendered.subscribe((component: ListFollowComponent) => {
    component.scrollContainerSelector = modalClassName;
    component.followType = followType;
    component.userId = isExternal ? user.id : 'me';
    component.totalItems =
      followType === 'FOLLOWER' ? user.followers_count : user.followings_count;
  });
}

export function configureRankingModal(
  modal: ModalService,
  top3: Top3,
  information: any,
  client: User,
  modalClassName: string
): Subscription {
  modal.setDialogComponent(WatchRankingModal);
  return modal.onModalRendered.subscribe((component: WatchRankingModal) => {
    component.videoId = information.id;
    component.userLogged = client;
    component.userRanked = top3.me;
    component.top3 = top3;
    component.scrollContainerSelector = `.${modalClassName} div.p-dialog-content`;
    component.sport = information.sport;
    component.activityType = information.type;
  });
}

export function configureStravaModal(
  modal: ModalService,
  sectionsStrava: StravaSection[]
): Subscription {
  modal.setDialogComponent(WatchStravaSectionsModalComponent);
  return modal.onModalRendered.subscribe(
    (component: WatchStravaSectionsModalComponent) => {
      component.sectionsStrava = sectionsStrava;
    }
  );
}

export function configureLikesModal(
  modal: ModalService,
  client: User,
  information: any,
  modalClassName: string,
  onLikeClick: EventEmitter<Information>
): Subscription {
  modal.setDialogComponent(WatchLikeList);
  return modal.onModalRendered.subscribe((component: WatchLikeList) => {
    component.currentUser = client;
    component.type = information.type;
    component.title = information.name;
    component.liked = new BehaviorSubject<boolean>(information.has_liked);
    component.likeCount = new BehaviorSubject<number>(information.likes_count);
    component.scrollContainerSelector = `.${modalClassName} div.p-dialog-content`;
    component.videoId = information.id;
    component.likeClick?.subscribe(() => {
      const likeCountValue: number = component.likeCount.value;
      component.likeCount.next(
        component.liked.value ? likeCountValue - 1 : likeCountValue + 1
      );
      component.liked.next(!component.liked.value);

      onLikeClick.emit(information);
      information.has_liked
        ? (information.likes_count -= 1)
        : (information.likes_count += 1);
      information.has_liked = !information.has_liked;
    });
  });
}

export function configureCommentsModal(
  modal: ModalService,
  client: any,
  information: any,
  modalClassName: string,
  data: any,
  comments$: BehaviorSubject<Comment[]>
): Subscription {
  modal.setDialogComponent(WatchCommentList);
  return modal.onModalRendered.subscribe((component: WatchCommentList) => {
    component.user = client;
    if (data?.focusCommentId !== undefined) {
      component.focusCommentId = data.focusCommentId;
    }
    component.title = information.name;
    component.commentType = information.type;
    component.commentsCount = information.comments_count;
    component.scrollContainerSelector = `.${modalClassName} div.p-dialog-content`;
    component.initList(information.id);
    component.commentType = information.type;
    component.commentAdded.subscribe((res: any) => {
      const comment: any = {
        body: res.body,
        created_at: new Date().getTime() / 1000,
        parent_id: null,
        reply_count: 0,
        user: client,
      };
      comments$.next([...comments$.getValue(), comment]);
    });
  });
}

export function configurePlaylistModal(
  modal: ModalService,
  information: any,
  toasterService: ToasterService,
  translate: TranslateService
): Subscription {
  modal.setDialogComponent(WatchPlaylistModal);
  return modal.onModalRendered.subscribe((component: WatchPlaylistModal) => {
    component.videoId = information.id;
    component.type = information.type;
    component.newPlaylist?.subscribe(() => {
      component.videoId = information.id;
      modal.setDialogComponent(WatchNewPlaylistModalComponent);
      return modal.onModalRendered.subscribe(
        (component: WatchNewPlaylistModalComponent) => {
          component.close?.subscribe((playlistName: string) => {
            modal.hide();
            toasterService.success(
              null,
              playlistName +
                ' ' +
                translate.instant(
                  'watch.playlist_modal.create_new_playlist_modal.toast'
                )
            );
          });
        }
      );
    });
  });
}

export function configureReportModal(
  modal: ModalService,
  information: any
): Subscription {
  modal.setDialogComponent(WatchReportModal);
  return modal.onModalRendered.subscribe((component: WatchReportModal) => {
    component.workoutReport = information.type === 'workout';
    component.videoReport = information.type === 'scenic';
    component.id = information.id;
    component.userId = information.author.id;
    component.type = information.type;
    component.cancel.subscribe(() => {
      modal.hide();
    });
  });
}

export function configureConnectionModal(
  modal: ModalService,
  loginService: SwarmLoginService
): Subscription {
  modal.setDialogComponent(ConnectionModalComponent);
  return modal.onModalRendered.subscribe(
    (component: ConnectionModalComponent) => {
      component.login.subscribe(() => {
        modal.hide();
        loginService.redirectToOauth();
      });
      component.signup.subscribe(() => {
        modal.hide();
        loginService.redirectToOauthCreateAccount();
      });
    }
  );
}

export function configurePaymentFormModal(modal: ModalService): Subscription {
  modal.setDialogComponent(BankCardFormModalComponent);
  return modal.onModalRendered.subscribe(
    (component: BankCardFormModalComponent) => {
      component.saveResult?.subscribe(() => modal.hide());
    }
  );
}

export function configureFiltersModal(
  modal: ModalService,
  isSearch: boolean,
  modeName: ModeNamesEnum
): Subscription {
  modal.setDialogComponent(AllFiltersComponent);
  return modal.onModalRendered.subscribe((component: AllFiltersComponent) => {
    component.reset({
      isSearch: isSearch,
      modeName: modeName,
    });
  });
}

export function configureUserDescriptionModal(
  modal: ModalService,
  user: User
): Subscription {
  modal.setDialogComponent(UserDescriptionComponent);
  return modal.onModalRendered.subscribe(
    (component: UserDescriptionComponent) => {
      component.user = user;
    }
  );
}
